import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  booleanAttribute,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';

type MultiSelectOptionRecord = Record<string, unknown>;
export type CxMultiSelectValue<T extends MultiSelectOptionRecord> = Array<T | T[keyof T]>;
export interface CxMultiSelectSelectedItemsContext<T extends MultiSelectOptionRecord> {
  $implicit: T[];
  removeChip: (value: unknown, event: Event) => void;
}

let nextMultiSelectId = 0;

@Component({
  selector: 'cx-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, MultiSelectModule],
  templateUrl: './cx-multi-select.component.html',
  styleUrls: ['./cx-multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CxMultiSelectComponent<
  T extends MultiSelectOptionRecord = MultiSelectOptionRecord,
> implements ControlValueAccessor {
  @Input() options: ReadonlyArray<T> = [];
  @Input() optionLabel = 'label';
  @Input() optionValue?: string;
  @Input({ transform: booleanAttribute }) group = false;
  @Input() optionGroupLabel?: string;
  @Input() optionGroupChildren?: string;
  @Input() placeholder?: string;
  @Input() appendTo: unknown = 'body';
  @Input() selectedItemsLabel?: string;
  @Input() maxSelectedLabels = 3;
  @Input({ transform: booleanAttribute }) loading = false;
  @Input({ transform: booleanAttribute }) filter = false;
  @Input() filterBy?: string;
  @Input() scrollHeight = '200px';
  @Input({ transform: booleanAttribute }) showClear = false;
  @Input({ transform: booleanAttribute }) showHeader = false;
  @Input() headerTemplate: TemplateRef<unknown> | null = null;
  @Input() itemTemplate: TemplateRef<{ $implicit: T }> | null = null;
  @Input() selectedItemsTemplate: TemplateRef<CxMultiSelectSelectedItemsContext<T>> | null = null;
  @Input({ transform: booleanAttribute }) invalid = false;

  @Input({ transform: booleanAttribute })
  set disabled(value: boolean) {
    this.inputDisabled = value;
    this.cdr.markForCheck();
  }
  get disabled(): boolean {
    return this.inputDisabled;
  }

  @Output() readonly valueChange = new EventEmitter<CxMultiSelectValue<T>>();

  readonly inputId = `cx-multi-select-${nextMultiSelectId++}`;
  readonly ngControl = inject(NgControl, { optional: true, self: true });
  private readonly cdr = inject(ChangeDetectorRef);

  private inputDisabled = false;
  private controlDisabled = false;
  private value: CxMultiSelectValue<T> = [];
  private onChange: (value: CxMultiSelectValue<T>) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get isDisabled(): boolean {
    return this.inputDisabled || this.controlDisabled;
  }

  get isInvalid(): boolean {
    if (this.invalid) {
      return true;
    }
    const control = this.ngControl?.control;
    return !!control?.invalid && !!(control.touched || control.dirty);
  }

  get multiValue(): CxMultiSelectValue<T> {
    return this.value;
  }

  get showPanelHeader(): boolean {
    return this.showHeader || this.filter || !!this.headerTemplate;
  }

  onSelectionChange(value: unknown): void {
    if (this.isDisabled) {
      return;
    }

    const normalized = this.normalizeMultiValue(value);
    this.updateValue(normalized);
  }

  onBlur(): void {
    this.onTouched();
  }

  writeValue(value: CxMultiSelectValue<T> | null): void {
    this.value = this.normalizeMultiValue(value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: CxMultiSelectValue<T>) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  private updateValue(value: CxMultiSelectValue<T>): void {
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
    this.cdr.markForCheck();
  }

  private normalizeMultiValue(value: unknown): CxMultiSelectValue<T> {
    if (!Array.isArray(value)) {
      return [];
    }

    return value as CxMultiSelectValue<T>;
  }
}
